import React from 'react';

// Types for fundamentals data
export interface FinancialData {
  tickerBasicsId: number;
  statsKey: string;
  statsValue: number;
  statsUnit: string;
  category: string;
  timeframe: string;
  year: number;
  quarter: number;
  periodEndDate: Date;
}

export interface RatioData {
  tickerBasicsId: number;
  ratioKey: string;
  ratioValue: number;
  ratioCategory: string | null;
  timeframe: string;
  year: number;
  quarter: number;
  periodEndDate: Date;
}

export interface QuarterlyColumn {
  label: string;
  date: string;
  year: number;
  quarter: number;
}

export interface AnnualColumn {
  label: string;
  date: string;
  year: number;
}

/**
 * Generate quarterly columns from financials and ratios data
 * @param financials - Financial data array
 * @param ratios - Ratio data array
 * @param latestDailyDate - Optional latest date from ticker_daily table (used for first column if current quarter has data)
 */
export const generateQuarterlyColumns = (
  financials: FinancialData[] | any[],
  ratios: RatioData[] | any[],
  latestDailyDate?: Date | string | null
): QuarterlyColumn[] => {
  const useRatiosOnly = !financials || financials.length === 0;
  const allData = useRatiosOnly ? [...ratios] : [...financials, ...ratios];
  const uniquePeriods = new Map<string, { year: number; quarter: number; periodEndDate: Date }>();
  
  allData.forEach((item: any) => {
    const key = `${item.year}-Q${item.quarter}`;
    if (!uniquePeriods.has(key)) {
      const raw = item as any;
      const sourceDateStr = useRatiosOnly ? raw?.date : (raw?.periodEndDate ?? raw?.date);
      const maybeDate = sourceDateStr ? new Date(sourceDateStr) : undefined;
      if (maybeDate && !isNaN(maybeDate.getTime())) {
        uniquePeriods.set(key, { year: item.year as number, quarter: item.quarter as number, periodEndDate: maybeDate });
      }
    }
  });

  const sortedPeriods = Array.from(uniquePeriods.values())
    .sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.quarter - a.quarter;
    });

  const now = new Date();
  const nyTime = new Date(now.toLocaleString("en-US", {timeZone: "America/New_York"}));
  const currentYear = nyTime.getFullYear();
  const currentQuarter = Math.ceil((nyTime.getMonth() + 1) / 3);
  const todayNY = nyTime.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });

  const columns: QuarterlyColumn[] = [];

  const currentQuarterHasData = sortedPeriods.some(period => 
    period.year === currentYear && period.quarter === currentQuarter
  );

  if (currentQuarterHasData) {
    // Use latestDailyDate if provided, otherwise use today's date
    let firstColumnDate: string;
    if (latestDailyDate) {
      const dailyDate = typeof latestDailyDate === 'string' ? new Date(latestDailyDate) : latestDailyDate;
      if (dailyDate && !isNaN(dailyDate.getTime())) {
        firstColumnDate = dailyDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
      } else {
        firstColumnDate = todayNY;
      }
    } else {
      firstColumnDate = todayNY;
    }
    
    columns.push({
      label: firstColumnDate,
      date: firstColumnDate,
      year: currentYear,
      quarter: currentQuarter
    });
  }

  sortedPeriods.forEach((period) => {
    if (currentQuarterHasData && period.year === currentYear && period.quarter === currentQuarter) {
      return;
    }
    const label = period.periodEndDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    columns.push({ label, date: label, year: period.year, quarter: period.quarter });
  });

  return columns;
};

/**
 * Generate annual columns from financials and ratios data
 */
export const generateAnnualColumns = (
  financials: FinancialData[] | any[],
  ratios: RatioData[] | any[]
): AnnualColumn[] => {
  const useRatiosOnly = !financials || financials.length === 0;
  const allData = useRatiosOnly ? [...ratios] : [...financials, ...ratios];
  const uniqueYears = new Map<number, Date>();
  
  allData.forEach((item: any) => {
    if (!uniqueYears.has(item.year)) {
      const raw = item as any;
      const sourceDateStr = useRatiosOnly ? raw?.date : (raw?.periodEndDate ?? raw?.date);
      const maybeDate = sourceDateStr ? new Date(sourceDateStr) : undefined;
      if (maybeDate && !isNaN(maybeDate.getTime())) {
        uniqueYears.set(item.year, maybeDate);
      }
    }
  });

  const sortedYears = Array.from(uniqueYears.entries())
    .sort((a, b) => b[0] - a[0]);

  return sortedYears.map(([year, periodEndDate]) => ({
    label: periodEndDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
    date: periodEndDate.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }),
    year
  }));
};

/**
 * Get value for a specific statistic and period from financials/ratios data
 */
export const getFundamentalsValue = (
  statisticKey: string,
  column: { year: number; quarter?: number },
  financialData: FinancialData[] | any[],
  ratioData: RatioData[] | any[],
  timeframe: 'quarterly' | 'annual'
): number | null => {
  const financialItem = financialData.find((d: any) => 
    d.statsKey === statisticKey && 
    d.year === column.year && 
    (timeframe === 'annual' || d.quarter === column.quarter)
  );
  
  if (financialItem) {
    return financialItem.statsValue;
  }
  
  const ratioItem = ratioData.find((d: any) => 
    d.ratioKey === statisticKey && 
    d.year === column.year && 
    (timeframe === 'annual' || d.quarter === column.quarter)
  );
  
  return ratioItem ? ratioItem.ratioValue : null;
};

/**
 * Format a fundamental value based on its type
 */
export const formatFundamentalsValue = (
  value: number | null,
  type: string,
  statisticKey?: string,
  isLoading?: boolean
): string | React.ReactNode => {
  if (isLoading) {
    return <div className="animate-pulse bg-gray-200 dark:bg-border-color h-4 w-16 rounded"></div>;
  }
  
  if (value === null || value === undefined) return <span className="text-gray-400 dark:text-text-secondary">-</span>;
  
  if (type === 'ratio') {
    const alwaysPercentageStats = [
      'dividend_yield',
      'qoq_revenue_growth', 'qoq_net_income_growth', 'qoq_eps_growth',
      'yoy_revenue_growth', 'yoy_net_income_growth', 'yoy_eps_growth',
      'gross_margin', 'operating_margin', 'net_margin',
      'roa', 'roe', 'roic', 'cash_flow_margin', 'fcf_yield'
    ];
    
    const neverPercentageStats = [
      'pe_ratio', 'forward_pe_ratio', 'ps_ratio', 'pb_ratio', 'peg_ratio',
      'current_ratio', 'quick_ratio', 'book_value_per_share',
      'ocf_ratio',
      'ev_fcf_ratio', 'p_fcf_ratio'
    ];
    
    if (statisticKey && alwaysPercentageStats.includes(statisticKey)) {
      return value.toFixed(2) + '%';
    } else if (statisticKey && neverPercentageStats.includes(statisticKey)) {
      return value.toFixed(2);
    } else {
      if (value > 1) {
        return value.toFixed(2);
      } else {
        return (value * 100).toFixed(2) + '%';
      }
    }
  } else if (type === 'financial') {
    if (Math.abs(value) >= 1e9) {
      return `$${(value / 1e9).toFixed(2)}B`;
    } else if (Math.abs(value) >= 1e6) {
      return `$${(value / 1e6).toFixed(2)}M`;
    } else if (Math.abs(value) >= 1e3) {
      return `$${(value / 1e3).toFixed(2)}K`;
    } else {
      return `$${value.toFixed(2)}`;
    }
  }
  
  return value.toString();
};

/**
 * Format comparison value for tooltips
 */
const formatComparisonValue = (value: number, statisticKey: string): string => {
  if (statisticKey === 'peg_ratio') {
    return value.toFixed(2);
  } else if (statisticKey === 'current_ratio' || statisticKey === 'quick_ratio') {
    return value.toFixed(2);
  } else if (statisticKey.includes('growth') || statisticKey.includes('margin') || 
             statisticKey === 'dividend_yield' || statisticKey === 'roa' || statisticKey === 'roe' || statisticKey === 'roic' ||
             statisticKey === 'cash_flow_margin') {
    return value.toFixed(2) + '%';
  } else if (statisticKey === 'ocf_ratio' || statisticKey === 'fcf_yield') {
    return value.toFixed(2);
  } else if (statisticKey.includes('pe_ratio') || statisticKey === 'ps_ratio' || 
             statisticKey === 'pb_ratio' || statisticKey === 'book_value_per_share' ||
             statisticKey === 'ev_fcf_ratio' || statisticKey === 'p_fcf_ratio') {
    return value.toFixed(2);
  } else {
    if (Math.abs(value) >= 1e9) {
      return '$' + (value / 1e9).toFixed(2) + 'B';
    } else if (Math.abs(value) >= 1e6) {
      return '$' + (value / 1e6).toFixed(2) + 'M';
    } else if (Math.abs(value) >= 1e3) {
      return '$' + (value / 1e3).toFixed(2) + 'K';
    } else {
      return '$' + value.toFixed(2);
    }
  }
};

/**
 * Get arrow tooltip text based on comparison data
 */
export const getFundamentalsArrowTooltip = (
  statisticKey: string,
  currentValue: number | null,
  previousValue: number | null,
  timeframe: 'quarterly' | 'annual'
): string => {
  if (currentValue === null || currentValue === undefined || previousValue === null || previousValue === undefined) {
    return '';
  }

  if (timeframe === 'annual') {
    return `Compared to ${formatComparisonValue(previousValue, statisticKey)} the previous year`;
  } else {
    if (['pe_ratio', 'forward_pe_ratio', 'ps_ratio', 'pb_ratio', 'dividend_yield'].includes(statisticKey)) {
      return `Compared to ${formatComparisonValue(previousValue, statisticKey)} the previous quarter`;
    }
    if (['qoq_revenue_growth', 'qoq_net_income_growth', 'qoq_eps_growth', 'yoy_revenue_growth', 'yoy_net_income_growth', 'yoy_eps_growth'].includes(statisticKey)) {
      return `Compared to ${formatComparisonValue(previousValue, statisticKey)} one year ago`;
    }
    if (['gross_margin', 'operating_margin', 'net_margin', 'roa', 'roe', 'roic', 'revenues', 'gross_profit', 'operating_expense', 'sg_and_a', 'r_and_d', 'operating_income_loss', 'net_income_loss', 'diluted_earnings_per_share_adjusted'].includes(statisticKey)) {
      return `Compared to ${formatComparisonValue(previousValue, statisticKey)} the previous quarter`;
    }
    if (['current_ratio', 'quick_ratio', 'book_value_per_share', 'assets', 'total_debt', 'equity', 'current_assets', 'current_liabilities', 'inventory'].includes(statisticKey)) {
      return `Compared to ${formatComparisonValue(previousValue, statisticKey)} the previous quarter`;
    }
    if (['operating_cash_flow', 'investing_cash_flow', 'financing_cash_flow', 'end_cash_position', 'capital_expenditure', 'free_cash_flow', 'cash_flow_margin', 'ocf_ratio', 'fcf_yield', 'ev_fcf_ratio', 'p_fcf_ratio'].includes(statisticKey)) {
      return `Compared to ${formatComparisonValue(previousValue, statisticKey)} the previous quarter`;
    }
  }

  return '';
};

/**
 * Get arrow direction and color based on comparison logic
 */
export const getFundamentalsArrowInfo = (
  statisticKey: string,
  currentValue: number | null,
  previousValue: number | null,
  timeframe: 'quarterly' | 'annual'
): { direction: 'up' | 'down' | null, isGood: boolean } => {
  if (currentValue === null || currentValue === undefined || previousValue === null || previousValue === undefined) {
    return { direction: null, isGood: false };
  }

  if (timeframe === 'annual') {
    if (['pe_ratio', 'ps_ratio', 'pb_ratio', 'ev_fcf_ratio', 'p_fcf_ratio'].includes(statisticKey)) {
      if (currentValue < previousValue) return { direction: 'down', isGood: true };
      if (currentValue > previousValue) return { direction: 'up', isGood: false };
      return { direction: null, isGood: false };
    }
    
    if (statisticKey === 'dividend_yield') {
      if (currentValue > previousValue) return { direction: 'up', isGood: true };
      if (currentValue < previousValue) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }

    if (['yoy_revenue_growth', 'yoy_net_income_growth', 'yoy_eps_growth'].includes(statisticKey)) {
      if (currentValue > previousValue) return { direction: 'up', isGood: true };
      if (currentValue < previousValue) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }

    if (['gross_margin', 'operating_margin', 'net_margin', 'roa', 'roe', 'roic'].includes(statisticKey)) {
      if (currentValue > previousValue) return { direction: 'up', isGood: true };
      if (currentValue < previousValue) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }

    if (['revenues', 'gross_profit', 'operating_income_loss', 'net_income_loss', 'diluted_earnings_per_share_adjusted'].includes(statisticKey)) {
      if (currentValue > previousValue) return { direction: 'up', isGood: true };
      if (currentValue < previousValue) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }

    if (['operating_expense', 'sg_and_a', 'r_and_d'].includes(statisticKey)) {
      if (currentValue < previousValue) return { direction: 'down', isGood: true };
      if (currentValue > previousValue) return { direction: 'up', isGood: false };
      return { direction: null, isGood: false };
    }

    if (['current_ratio', 'quick_ratio', 'book_value_per_share'].includes(statisticKey)) {
      if (currentValue > previousValue) return { direction: 'up', isGood: true };
      if (currentValue < previousValue) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }

    if (['assets', 'equity', 'current_assets', 'current_liabilities', 'inventory', 'total_debt'].includes(statisticKey)) {
      if (statisticKey === 'current_liabilities' || statisticKey === 'total_debt') {
        if (currentValue < previousValue) return { direction: 'down', isGood: true };
        if (currentValue > previousValue) return { direction: 'up', isGood: false };
        return { direction: null, isGood: false };
      }
      if (currentValue > previousValue) return { direction: 'up', isGood: true };
      if (currentValue < previousValue) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }

    if (['operating_cash_flow', 'investing_cash_flow', 'financing_cash_flow', 'end_cash_position', 'capital_expenditure', 'free_cash_flow', 'cash_flow_margin', 'ocf_ratio', 'fcf_yield'].includes(statisticKey)) {
      if (statisticKey === 'capital_expenditure' || statisticKey === 'investing_cash_flow') {
        if (currentValue < previousValue) return { direction: 'down', isGood: true };
        if (currentValue > previousValue) return { direction: 'up', isGood: false };
        return { direction: null, isGood: false };
      }
      if (currentValue > previousValue) return { direction: 'up', isGood: true };
      if (currentValue < previousValue) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }
  } else {
    if (['pe_ratio', 'forward_pe_ratio', 'ps_ratio', 'pb_ratio'].includes(statisticKey)) {
      if (currentValue < previousValue) return { direction: 'down', isGood: true };
      if (currentValue > previousValue) return { direction: 'up', isGood: false };
      return { direction: null, isGood: false };
    }

    if (['ev_fcf_ratio', 'p_fcf_ratio'].includes(statisticKey)) {
      if (currentValue < previousValue) return { direction: 'down', isGood: true };
      if (currentValue > previousValue) return { direction: 'up', isGood: false };
      return { direction: null, isGood: false };
    }
    
    if (statisticKey === 'dividend_yield') {
      if (currentValue > previousValue) return { direction: 'up', isGood: true };
      if (currentValue < previousValue) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }

    if (['qoq_revenue_growth', 'qoq_net_income_growth', 'qoq_eps_growth', 'yoy_revenue_growth', 'yoy_net_income_growth', 'yoy_eps_growth'].includes(statisticKey)) {
      if (currentValue > previousValue) return { direction: 'up', isGood: true };
      if (currentValue < previousValue) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }

    if (['gross_margin', 'operating_margin', 'net_margin', 'roa', 'roe', 'roic'].includes(statisticKey)) {
      if (currentValue > previousValue) return { direction: 'up', isGood: true };
      if (currentValue < previousValue) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }

    if (['revenues', 'gross_profit', 'operating_income_loss', 'net_income_loss', 'diluted_earnings_per_share_adjusted'].includes(statisticKey)) {
      if (currentValue > previousValue) return { direction: 'up', isGood: true };
      if (currentValue < previousValue) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }

    if (['operating_expense', 'sg_and_a', 'r_and_d'].includes(statisticKey)) {
      if (currentValue < previousValue) return { direction: 'down', isGood: true };
      if (currentValue > previousValue) return { direction: 'up', isGood: false };
      return { direction: null, isGood: false };
    }

    if (['current_ratio', 'quick_ratio', 'book_value_per_share'].includes(statisticKey)) {
      if (currentValue > previousValue) return { direction: 'up', isGood: true };
      if (currentValue < previousValue) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }

    if (['assets', 'equity', 'current_assets', 'current_liabilities', 'inventory', 'total_debt'].includes(statisticKey)) {
      if (statisticKey === 'current_liabilities' || statisticKey === 'total_debt') {
        if (currentValue < previousValue) return { direction: 'down', isGood: true };
        if (currentValue > previousValue) return { direction: 'up', isGood: false };
        return { direction: null, isGood: false };
      }
      if (currentValue > previousValue) return { direction: 'up', isGood: true };
      if (currentValue < previousValue) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }

    if (['operating_cash_flow', 'investing_cash_flow', 'financing_cash_flow', 'end_cash_position', 'capital_expenditure', 'free_cash_flow', 'cash_flow_margin', 'ocf_ratio', 'fcf_yield'].includes(statisticKey)) {
      if (statisticKey === 'capital_expenditure' || statisticKey === 'investing_cash_flow') {
        if (currentValue < previousValue) return { direction: 'down', isGood: true };
        if (currentValue > previousValue) return { direction: 'up', isGood: false };
        return { direction: null, isGood: false };
      }
      if (currentValue > previousValue) return { direction: 'up', isGood: true };
      if (currentValue < previousValue) return { direction: 'down', isGood: false };
      return { direction: null, isGood: false };
    }
  }

  return { direction: null, isGood: false };
};

